import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import Button from "@/components/common/Button";
import FormField from "@/components/common/FormField";
import { Layout } from "@/components/Layout/Layout";
import { MainHeading } from "@/components/common/MainHeading";
import CommonTable, { TableColumn } from "@/components/common/CommonTable";
import {
    IAddVaientForm,
    IVarientsResponse,
} from "@/interfaces/Varient/varient.interface";
import { IProduct, IProductResponse } from "@/interfaces/Product/product.interface";
import { formatRand, showToastNotification } from "@/utility/helper";
import { errorTypes } from "@/utility/validation";
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Search } from "lucide-react";
import { useFilter } from "@/hooks/useFilter";
import { IFilter, IPageInfo } from "@/interfaces/common.interface";
import { GetFilterUrl } from "@/utility/helper";
import Pagination from "@/components/common/Pagination";
import { useActiveRole } from "@/hooks/useActiveRole";
import { RootState, useAppSelector } from "@/redux/store";
import { usePermissions } from "@/hooks/usePermissions";

export const AddVarients: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;
    const navigate = useNavigate();
    const [products, setProducts] = useState<IProduct[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [pageMetadata, setPageMetadata] = useState<IPageInfo>({
        limit: 5,
        page: 1,
        total: 0,
        totalPages: 0,
    });
    const { isAdmin } = usePermissions();
    const { filters, debouncedFilters, updateFilter, handlePageChange } = useFilter<IFilter>({
        search: '',
        page: 1,
        limit: 5,
        ...(isAdmin ? { isGlobal: "1" } : { isGlobal: "0" }),
    });

    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { errors, isSubmitting },
        setValue,
        watch,
    } = useForm<IAddVaientForm>({
        defaultValues: {
            name: "",
            price: null,
            unit: null,
            productId: "",
            status: "active",
            description: "",
        }
    });

    const selectedProductId = watch("productId");

    const user = useAppSelector((state: RootState) => state.user.user);

    const fetchProducts = async () => {
        setLoadingProducts(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);
            let url = `${API_URL + API_ENDPOINT.products}?${queryParams}`;
            // If store manager, filter by userId
            if (!isAdmin && user?.id) {
                url += `&userId=${user.id}`;
            }

            const response = await callApi<IProductResponse>(url, { method: 'GET' });
            setProducts(response?.data?.products || []);
            setPageMetadata(
                response?.data?.pageInfo || { limit: 5, page: 1, total: 0, totalPages: 0 }
            );
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoadingProducts(false);
        }
    };

    const fetchVarientDetails = async () => {
        if (!isEditMode) return;
        setLoadingDetails(true);
        try {
            const response = await callApi<any>(`${API_URL}${API_ENDPOINT.varients}/${id}`, { method: 'GET' });
            const varient = response?.data;
            reset({
                name: varient.name,
                price: Number(varient.price),
                unit: varient.unit,
                status: varient.status,
                productId: varient.productId,
                description: varient.description || "",
            });
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoadingDetails(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, [debouncedFilters.page, debouncedFilters.limit, debouncedFilters.search]);

    useEffect(() => {
        if (isEditMode) fetchVarientDetails();
    }, [id]);

    const onSubmit = async (data: IAddVaientForm) => {
        if (!data.productId) {
            showToastNotification("danger", "Please select a product first");
            return;
        }

        try {
            const url = isEditMode
                ? `${API_URL}${API_ENDPOINT.varients}/${id}`
                : `${API_URL}${API_ENDPOINT.varients}`;

            const method = isEditMode ? "PATCH" : "POST";

            const trimmedData = {
                ...data,
                name: data.name?.trim(),
                unit: data.unit?.trim(),
                description: data.description?.trim(),
            };

            const response = await callApi<IVarientsResponse>(url, {
                method,
                data: trimmedData,
            });
            showToastNotification("success", response?.message);
            navigate(-1);
        } catch (error) {
            ThrowError(error);
        }
    };

    const columns: TableColumn<IProduct>[] = [
        {
            key: "select",
            header: "Select",
            render: (item) => (
                <input
                    type="radio"
                    name="selectedProduct"
                    checked={selectedProductId === item.id}
                    onChange={() => setValue("productId", item.id, { shouldValidate: true })}
                    className="w-4 h-4 text-primary focus:ring-primary border-gray-300"
                />
            )
        },
        {
            key: "image",
            header: "Product",
            render: (item) => (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-secondary/20 overflow-hidden flex-shrink-0 border border-secondary/10">
                        {item.media && item.media[0] ? (
                            <img
                                src={item.media[0].url.startsWith('http') ? item.media[0].url : `${API_URL}/${item.media[0].url}`}
                                alt={item.name}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-[8px] text-muted-foreground">No Img</div>
                        )}
                    </div>
                    <span className="font-medium text-xs text-foreground line-clamp-1">{item.name}</span>
                </div>
            )
        },
        {
            key: "category",
            header: "Category",
            render: (item) => <span className="text-xs">{item.category?.name || '---'}</span>
        },
        {
            key: "price",
            header: "Price",
            render: (item) => (
                <div className="font-semibold text-primary">
                    {formatRand(item.price)}
                </div>
            )
        },
    ];

    if (loadingDetails) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <MainHeading
                title={isEditMode ? "Edit Varient" : "Add Varients"}
                description="Configure variants and link them to products"
                showBack
                onBack={() => navigate(-1)}
            />

            <div className="mt-8 grid grid-cols-1 xl:grid-cols-12 gap-8 pb-20">
                {/* ── Product Selection ── */}
                <div className="xl:col-span-7 space-y-6">
                    <div className="bg-card rounded-2xl border p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-sm font-bold text-foreground">Select Product</h3>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                <input
                                    type="text"
                                    placeholder="Search products..."
                                    className="h-8 pl-9 pr-4 rounded-md border bg-background text-xs w-48 focus:outline-none focus:ring-1 focus:ring-primary transition-all"
                                    value={filters.search}
                                    onChange={(e) => updateFilter({ search: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="min-h-[300px]">
                            <CommonTable
                                columns={columns}
                                data={products}
                                loading={loadingProducts}
                                emptyMessage="No products found to link this variant."
                            />
                        </div>

                        <div className="mt-4">
                            <Pagination
                                currentPage={debouncedFilters.page || 1}
                                totalPages={pageMetadata.totalPages}
                                totalItems={pageMetadata.total}
                                itemsPerPage={pageMetadata.limit}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Variant Form ── */}
                <div className="xl:col-span-5 space-y-6">
                    <div className="bg-card rounded-2xl border p-6 shadow-sm sticky top-6">
                        <h3 className="text-sm font-bold text-foreground mb-6">Varient Details</h3>
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                label="Varient Name"
                                name="name"
                                type="text"
                                requiredIcon={true}
                                placeholder="e.g. Small / 250g"
                                error={errors.name?.message}
                                {...register("name", { ...errorTypes.required("Varient Name") })}
                            />
                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    label="Price"
                                    name="price"
                                    type="number"
                                    min={0}
                                    placeholder="Enter price"
                                    error={errors.price?.message}
                                    {...register("price", { ...errorTypes.required("Price") })}
                                    requiredIcon={true}
                                />
                                <FormField
                                    label="Unit"
                                    name="unit"
                                    type="text"
                                    placeholder="Enter unit"
                                    error={errors.unit?.message}
                                    {...register("unit", { ...errorTypes.required("Unit") })}
                                    requiredIcon={true}
                                />
                            </div>

                            <FormField
                                label="Description"
                                name="description"
                                type="textarea"
                                rows={2}
                                placeholder="Brief description of the variant..."
                                error={errors.description?.message}
                                {...register("description")}
                            />

                            {errors.productId && (
                                <p className="text-xs font-bold text-destructive animate-pulse py-2">
                                    ⚠️ Please select a product from the list on the left.
                                </p>
                            )}

                            <div className="flex flex-col gap-3 pt-4">
                                <Button
                                    type="submit"
                                    className="w-full h-11 shadow-lg shadow-primary/20"
                                    isLoading={isSubmitting}
                                    loadingText={isEditMode ? "Saving Changes..." : "Creating Varient..."}
                                >
                                    {isEditMode ? "Update Varient" : "Create & Link Varient"}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full h-11"
                                    onClick={() => navigate(-1)}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
